import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { updateClient } from '../services/clientService';

export default function EditClientModal({ customer, onClose, onSave, actor }) {
  const surnames = (customer.surname || customer.fullSurname || '').trim().split(' ');
  const initialSurname = surnames[0] || '';
  const initialSecondSurname = surnames.slice(1).join(' ');

  const [formData, setFormData] = useState({
    name: customer.name || '',
    surname: initialSurname,
    secondSurname: initialSecondSurname,
    email: customer.email || '',
    phone: customer.phone || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  function handleChange(event) {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');

    const payload = {
      name: formData.name,
      surname: [formData.surname, formData.secondSurname].filter(Boolean).join(' ').trim(),
      email: formData.email.trim(),
      phone: formData.phone,
      performedByUserId: actor?.id || null,
      performedByName: actor?.name || actor?.fullName || actor?.email || 'Operador',
    };

    try {
      const updatedCustomer = await updateClient(customer.id, payload);
      onSave(updatedCustomer);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Error al actualizar el cliente. Revisa los datos.';
      setError(typeof msg === 'string' ? msg : 'Error al actualizar el cliente.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          className="w-full max-w-md overflow-hidden rounded-[32px] bg-white shadow-2xl"
        >
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-slate-900">Editar Cliente</h3>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <p className="text-sm font-medium text-red-600 bg-red-50 p-3 rounded-xl">{error}</p>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Primer apellido</label>
                <input
                  type="text"
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Segundo apellido (opcional)</label>
                <input
                  type="text"
                  name="secondSurname"
                  value={formData.secondSurname}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Correo electrónico</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
            </div>

            <div className="pt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-2xl px-5 py-3 font-medium text-slate-600 hover:bg-slate-100 transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busy}
                className="rounded-2xl bg-blue-600 px-5 py-3 font-medium text-white hover:bg-blue-700 transition shadow-md disabled:opacity-50"
              >
                {busy ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
